import React, { useState, type FormEvent, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { resetPassword as resetPasswordService } from '../services/authService';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No reset token provided. Request a new password reset link.');
    }
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!token) {
      setError('Invalid or missing reset token.');
      return;
    }
    if (!password || !confirmPassword) {
      setError('Both password fields are required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (new TextEncoder().encode(password).length > 72) {
      setError('Password is too long (bcrypt limit is 72 bytes).');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password) || !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError('Password must include uppercase, lowercase, a number, and a special character.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetPasswordService(token, { password });
      setSuccessMessage(response.message || 'Password has been reset. You can sign in now.');
      setPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      console.error('Reset password error:', err);
      const message = err instanceof Error ? err.message : 'Failed to reset password. The link may be invalid or expired.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, md: 6 } }}>
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/images/DIRTcarLogo.png" alt="DIRTcar" style={{ height: 48, width: 'auto' }} />
          </Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
            Reset password
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Choose a new password for your account.
          </Typography>

          {!token && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={token ? () => setError(null) : undefined}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {!successMessage && token && (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="New password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  autoFocus
                  required
                  fullWidth
                />
                <TextField
                  label="Confirm new password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" size="large" disabled={isLoading} fullWidth>
                  {isLoading ? 'Resetting' : 'Reset password'}
                </Button>
              </Stack>
            </Box>
          )}

          {successMessage && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <MuiLink component={RouterLink} to="/login" variant="body2" underline="hover">
                Continue to sign in
              </MuiLink>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
